import { browserStorage, commandBody, recoveryPrefix, clearConfirmedDraft, validateDraftReference, type DraftReference, type RecoveryStorage } from '../browser-recovery/storage.ts';
import { savedCommands } from '../browser-recovery/commands.ts';
export interface TransferAccount {
  userId: string;
  domain: string;
  organizationId?: string;
}
export interface TransferCommand {
  draft?: DraftReference;
  path: string;
  body: string;
  requestId: string;
}
export interface TransferReceipt {
  id: number;
  revision: number;
  action: string;
}
export class TransferHttpError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}
export async function transferRequest<T>(
  base: string,
  account: TransferAccount,
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('X-Shop-Domain', account.domain);
  if (account.organizationId) headers.set('X-Organization-Id', account.organizationId);
  // Hono mounts a child '/' at the prefix itself, without a trailing slash.
  const suffix = path === '/' ? '' : path.startsWith('/?') ? path.slice(1) : path;
  const response = await fetch(`${base}/api/transfer-orders${suffix}`, {
    ...options,
    headers,
    credentials: 'include',
    cache: 'no-store',
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data || data.success === false)
    throw new TransferHttpError(
      data?.error || 'The server response could not be confirmed.',
      response.ok ? 500 : response.status,
    );
  return data as T;
}
// Keep exact requests across browser restarts; never retry automatically.
export function createTransferCommands(base: string, account: TransferAccount, storage: RecoveryStorage = browserStorage) {
  const commands = savedCommands<TransferCommand, TransferReceipt>({
    storage, prefix: recoveryPrefix('transfer', base, account),
    validate: (value: unknown): asserts value is TransferCommand => {
      commandBody(value, account.userId);
      validateDraftReference((value as TransferCommand).draft, recoveryPrefix('draft', base, account));
      if (value.path !== '/' && !/^\/[1-9]\d*\/(?:edit|adopt|ship|receive|return|loss|restock|dispose|cancel)$/.test(value.path)) throw new Error('Invalid saved transfer route');
    },
    confirmed: command => clearConfirmedDraft(command.draft, storage),
    definitive: error => error instanceof TransferHttpError && [400,409,413,422].includes(error.status),
    send: async pending => {
      const result = await transferRequest<TransferReceipt & { success: boolean; request_id: string }>(base, account, pending.path, { method: 'POST', body: pending.body });
      const expectedAction = pending.path === '/' ? 'create' : pending.path.split('/').pop();
      const expectedId = Number(pending.path.split('/')[1]);
      const expectedRevision = expectedAction === 'create' ? 1 : Number(JSON.parse(pending.body).revision) + 1;
      if (result.success !== true || result.request_id !== pending.requestId || !Number.isSafeInteger(result.id) || result.id <= 0
        || !Number.isSafeInteger(result.revision) || result.revision !== expectedRevision || result.action !== expectedAction
        || (expectedAction !== 'create' && result.id !== expectedId)) throw new TransferHttpError('The save receipt could not be confirmed. Retry the saved request.', 500);
      return result;
    },
  });
  return { ...commands, submit: async (path: string, body: Record<string, unknown>, draft?: DraftReference) => {
    if (commands.snapshot().pending || commands.snapshot().busy) return null;
    const requestId = crypto.randomUUID();
    await commands.submit({ path, requestId, draft, body: JSON.stringify({ ...body, request_id: requestId, expected_user_id: account.userId }) });
    return commands.snapshot().success;
  } };
}
export type TransferCommands = ReturnType<typeof createTransferCommands>;
export function transferAccountKey(account: TransferAccount) {
  return JSON.stringify([account.userId, account.domain, account.organizationId || '']);
}
